/**
 * TokenPalettePopover - the caret-first token inserter. Opens from the inspector;
 * lists the catalog's tokens as searchable, source-grouped TokenChips; clicking a
 * chip inserts it at the cursor of the active section editor. AI-section tokens are
 * omitted (they are sections, set via the role switch, not inline prose tokens).
 */

import { Button, Popover, TextField, TokenChip } from '@protoss/ui';
import * as React from 'react';
import { humanizeToken } from '../../../../components/doc/previewSample';
import type { CatalogToken } from '../../lib/sowTemplateApi';

export interface TokenPalettePopoverProps {
  tokens: CatalogToken[];
  /** No active prose editor to insert into (e.g. a non-fixed section selected). */
  disabled?: boolean;
  onInsert: (token: CatalogToken) => void;
}

const GROUPS: Array<{ source: CatalogToken['source']; label: string }> = [
  { source: 'context', label: 'Fields & context' },
  { source: 'data', label: 'Estimate data' },
];

export function TokenPalettePopover({ tokens, disabled, onInsert }: TokenPalettePopoverProps) {
  const [query, setQuery] = React.useState('');
  const q = query.trim().toLowerCase();
  const insertable = tokens.filter((t) => t.source !== 'ai_section');
  const filtered = q
    ? insertable.filter(
        (t) =>
          t.token.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q) ||
          humanizeToken(t.token).toLowerCase().includes(q)
      )
    : insertable;

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <Button type="button" variant="outline" size="sm" disabled={disabled}>
          + Insert token
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="sow-token-palette dropdown-content"
          align="start"
          sideOffset={6}
          collisionPadding={8}
        >
          <TextField
            value={query}
            placeholder="Search tokens"
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="sow-token-palette__list">
            {GROUPS.map((g) => {
              const items = filtered.filter((t) => t.source === g.source);
              if (items.length === 0) return null;
              return (
                <div key={g.source} className="sow-token-palette__group">
                  <span className="sow-token-palette__group-label">{g.label}</span>
                  <div className="sow-token-palette__chips">
                    {items.map((t) => (
                      <TokenChip
                        key={t.token}
                        label={humanizeToken(t.token)}
                        source={t.source}
                        description={t.description}
                        onClick={() => onInsert(t)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 ? (
              <p className="sow-token-palette__empty">No tokens match.</p>
            ) : null}
            {disabled ? (
              <p className="sow-token-palette__empty">
                Select a fixed-text section to insert tokens into it.
              </p>
            ) : null}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
